/**
 * DialogoAssegnaNumero — «Assegna il numero all'azienda» (atlante cap. 264): quando Twilio ci consegna il numero chiesto
 * per un'azienda, un clic fa il resto e consegna il centralino. Il canone parte quel giorno.
 *
 * Tre momenti nella stessa finestra:
 *  1. PRIMA: il numero e l'anteprima di cosa succederà — i cinque passi, in ordine (sono davvero una sequenza), e come
 *     si accende il centralino il giorno della consegna. Se l'azienda non potrebbe pagarlo (nessun abbonamento né
 *     prova, pagamento in sospeso) lo dice, e chiede di spuntare «assegna comunque»: conviene non comprare il numero.
 *  2. DURANTE: l'attesa (Twilio può metterci qualche decina di secondi).
 *  3. DOPO: cosa ha fatto ogni passo (fatto, già fatto, non serve) e com'è andata la consegna. Un errore dice cosa è
 *     successo e, se i passi fatti si saltano, offre «Riprova».
 *
 * La finestra è della console (`Dialog`), mai una conferma nativa del browser. La callable è idempotente: rilanciarla
 * sullo stesso numero è sicuro.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Stack, TextField, Typography,
} from '@mui/material';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import { ultimoGiorno } from '../strumenti/testiStrumenti';
import {
  PASSI_ASSEGNAZIONE, assegnaNumeroAllAzienda, leggiErrore, type AllaConsegna, type RisultatoAssegnazione,
} from '../../services/consegneService';
import {
  COSA_FARA_IL_PASSO, ESITO_DEL_PASSO, NOME_DEL_PASSO, fraseAllaConsegna, fraseConsegnaFatta, messaggioDiErrore,
  numeroPlausibile,
} from './testiConsegna';

interface Props {
  aperto: boolean;
  onChiudi: () => void;
  tenantId: string;
  nomeAzienda: string;
  /** Cosa succederebbe consegnando oggi (la stessa regola del server); `null` se non si sa. */
  allaConsegna: AllaConsegna | null;
  /** Dopo un'assegnazione riuscita: chi apre la finestra rilegge ciò che serve. */
  onFatto: (r: RisultatoAssegnazione) => void;
}

const giorno = (iso: string) => ultimoGiorno(Date.parse(iso));

type Momento =
  | { tipo: 'prima' }
  | { tipo: 'durante' }
  | { tipo: 'dopo'; risultato: RisultatoAssegnazione }
  | { tipo: 'errore'; testo: string; siPuoRilanciare: boolean };

export const DialogoAssegnaNumero: React.FC<Props> = ({ aperto, onChiudi, tenantId, nomeAzienda, allaConsegna, onFatto }) => {
  const [numero, setNumero] = useState('');
  const [comunque, setComunque] = useState(false);
  const [momento, setMomento] = useState<Momento>({ tipo: 'prima' });

  useEffect(() => {
    if (!aperto) return;
    setNumero('');
    setComunque(false);
    setMomento({ tipo: 'prima' });
  }, [aperto, tenantId]);

  const consegna = fraseAllaConsegna(allaConsegna, giorno);
  const plausibile = numeroPlausibile(numero);
  const puoPartire = plausibile && (!consegna.attenzione || comunque);
  const lavora = momento.tipo === 'durante';

  const assegna = async () => {
    setMomento({ tipo: 'durante' });
    try {
      const risultato = await assegnaNumeroAllAzienda({ tenant_id: tenantId, numero: numero.trim() });
      setMomento({ tipo: 'dopo', risultato });
      onFatto(risultato);
    } catch (err) {
      const { testo, siPuoRilanciare } = messaggioDiErrore(leggiErrore(err));
      setMomento({ tipo: 'errore', testo, siPuoRilanciare });
    }
  };

  const etichetta = { variant: 'caption' as const, sx: { color: tokens.color.ash, display: 'block', mb: 0.75 } };

  return (
    <Dialog open={aperto} onClose={lavora ? undefined : onChiudi} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: tokens.font.display, fontWeight: 600 }}>Assegna il numero all’azienda</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: tokens.color.ash, mb: 2 }}>
          {nomeAzienda}: il numero che Twilio ha consegnato per questa azienda, con la sua pratica normativa già approvata.
        </Typography>

        {momento.tipo === 'dopo' ? (
          <Stack spacing={2}>
            <Alert severity="success">
              {momento.risultato.gia_tutto_fatto ? 'Era già tutto a posto: nessun passo ha dovuto cambiare niente.' : momento.risultato.riepilogo}
            </Alert>
            <Box>
              <Typography {...etichetta}>Numero</Typography>
              <Typography sx={{ fontFamily: tokens.font.mono }}>{momento.risultato.numero}</Typography>
            </Box>
            <Box>
              <Typography {...etichetta}>Cosa è stato fatto</Typography>
              <Stack spacing={1}>
                {momento.risultato.passi.map((p) => (
                  <Stack key={p.passo} direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ minWidth: 86 }}><SoftChip label={ESITO_DEL_PASSO[p.esito].testo} tone={ESITO_DEL_PASSO[p.esito].tono} /></Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{NOME_DEL_PASSO[p.passo]}</Typography>
                      <Typography variant="body2" sx={{ color: tokens.color.ash }}>{p.descrizione}</Typography>
                      {p.sid && (
                        <Typography variant="caption" sx={{ fontFamily: tokens.font.mono, color: tokens.color.mist, wordBreak: 'break-all' }}>
                          {p.sid}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Box>
            <Box>
              <Typography {...etichetta}>Il centralino</Typography>
              <Typography variant="body2">{fraseConsegnaFatta(momento.risultato.consegna, giorno)}</Typography>
            </Box>
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            <TextField
              label="Numero consegnato da Twilio"
              placeholder="+39 070 …"
              value={numero}
              onChange={(e) => {
                setNumero(e.target.value);
                // Un numero corretto dopo un rifiuto è una richiesta nuova: si riparte dall'anteprima.
                if (momento.tipo === 'errore') setMomento({ tipo: 'prima' });
              }}
              disabled={lavora}
              autoFocus
              fullWidth
              error={numero.trim() !== '' && !plausibile}
              helperText={
                numero.trim() !== '' && !plausibile
                  ? 'Scrivilo con il prefisso, solo cifre e spazi (es. +39 070 1234567).'
                  : 'Con o senza prefisso internazionale: il server lo porta nella forma +39….'
              }
              slotProps={{ htmlInput: { inputMode: 'tel', style: { fontFamily: tokens.font.mono } } }}
            />

            <Box>
              <Typography {...etichetta}>Cosa farà, in ordine (i passi già fatti si saltano)</Typography>
              <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
                {PASSI_ASSEGNAZIONE.map((p) => (
                  <li key={p}>
                    <Typography variant="body2">
                      <Box component="span" sx={{ fontWeight: 600 }}>{NOME_DEL_PASSO[p]}.</Box>{' '}
                      <Box component="span" sx={{ color: tokens.color.ash }}>{COSA_FARA_IL_PASSO[p]}</Box>
                    </Typography>
                  </li>
                ))}
                <li>
                  <Typography variant="body2">
                    <Box component="span" sx={{ fontWeight: 600 }}>Consegna.</Box>{' '}
                    <Box component="span" sx={{ color: tokens.color.ash }}>{consegna.testo}</Box>
                  </Typography>
                </li>
              </Box>
            </Box>

            {consegna.attenzione && (
              <Alert severity="warning">
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={<Checkbox checked={comunque} onChange={(e) => setComunque(e.target.checked)} disabled={lavora} />}
                  label="Assegna comunque: il centralino si accenderà rilanciando quando l’azienda potrà pagarlo"
                />
              </Alert>
            )}

            {momento.tipo === 'errore' && <Alert severity="error">{momento.testo}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {momento.tipo === 'dopo' ? (
          <Button variant="contained" disableElevation onClick={onChiudi}>Chiudi</Button>
        ) : (
          <>
            <Button onClick={onChiudi} disabled={lavora}>Annulla</Button>
            <Button
              variant="contained"
              disableElevation
              onClick={() => { void assegna(); }}
              disabled={lavora || !puoPartire || (momento.tipo === 'errore' && !momento.siPuoRilanciare)}
              startIcon={lavora ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {lavora ? 'Assegno il numero…' : momento.tipo === 'errore' ? 'Riprova' : 'Assegna il numero'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
